// page-notes-web/src/services/byok-config.js
// BYOK (Bring Your Own Key) Configuration Service for Web App
// Uses localStorage for storing API keys and settings

/**
 * Default models for each provider
 */
export const DEFAULT_MODELS = {
  openai: [
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    'gpt-3.5-turbo'
  ],
  anthropic: [
    'claude-sonnet-4-20250514',
    'claude-3-5-sonnet-20241022',
    'claude-3-5-haiku-20241022',
    'claude-3-opus-20240229'
  ],
  openrouter: [
    'openai/gpt-4o',
    'anthropic/claude-3.5-sonnet',
    'google/gemini-pro-1.5',
    'meta-llama/llama-3.1-70b-instruct'
  ],
  huggingface: [
    'meta-llama/Llama-3.2-3B-Instruct',
    'Qwen/Qwen2.5-72B-Instruct',
    'mistralai/Mistral-7B-Instruct-v0.3'
  ],
  ollama: [
    'llama3.2',
    'mistral',
    'codellama',
    'phi3'
  ]
};

/**
 * Provider metadata for UI
 */
export const PROVIDER_INFO = {
  openai: {
    name: 'OpenAI',
    keyUrl: 'https://platform.openai.com/api-keys',
    keyPlaceholder: 'sk-...',
    requiresKey: true
  },
  anthropic: {
    name: 'Anthropic',
    keyUrl: 'https://console.anthropic.com/settings/keys',
    keyPlaceholder: 'sk-ant-...',
    requiresKey: true
  },
  openrouter: {
    name: 'OpenRouter',
    keyUrl: 'https://openrouter.ai/keys',
    keyPlaceholder: 'sk-or-...',
    requiresKey: true
  },
  huggingface: {
    name: 'HuggingFace',
    keyUrl: 'https://huggingface.co/settings/tokens',
    keyPlaceholder: 'hf_...',
    requiresKey: true
  },
  ollama: {
    name: 'Ollama (Local)',
    keyUrl: 'https://ollama.com/download',
    keyPlaceholder: 'http://localhost:11434',
    requiresKey: false
  }
};

/**
 * Default state structure for BYOK
 */
const DEFAULT_STATE = {
  byokEnabled: false,
  activeProvider: 'openai',
  activeModel: '',
  providerCredentials: {
    openai: {
      apiKey: '',
      models: [...DEFAULT_MODELS.openai],
      selectedModel: 'gpt-4o-mini'
    },
    anthropic: {
      apiKey: '',
      models: [...DEFAULT_MODELS.anthropic],
      selectedModel: 'claude-3-5-sonnet-20241022'
    },
    openrouter: {
      apiKey: '',
      models: [...DEFAULT_MODELS.openrouter],
      selectedModel: 'openai/gpt-4o'
    },
    huggingface: {
      apiKey: '',
      models: [...DEFAULT_MODELS.huggingface],
      selectedModel: 'meta-llama/Llama-3.2-3B-Instruct'
    },
    ollama: {
      endpoint: 'http://localhost:11434',
      models: [...DEFAULT_MODELS.ollama],
      selectedModel: 'llama3.2'
    }
  }
};

/**
 * Storage key for BYOK state in localStorage
 */
const STORAGE_KEY = 'pageNotes_byokConfig';

/**
 * Internal state cache
 */
let state = { ...DEFAULT_STATE };
let stateLoaded = false;

/**
 * Merge stored state with defaults to ensure all required fields exist
 * @param {Object} storedState - The state retrieved from storage
 * @returns {Object} Merged state with all required fields
 */
function mergeWithDefaults(storedState) {
  const merged = {
    ...DEFAULT_STATE,
    ...storedState,
    providerCredentials: { ...DEFAULT_STATE.providerCredentials }
  };
  
  // Merge each provider's credentials
  for (const provider of Object.keys(DEFAULT_STATE.providerCredentials)) {
    merged.providerCredentials[provider] = {
      ...DEFAULT_STATE.providerCredentials[provider],
      ...(storedState.providerCredentials?.[provider] || {})
    };
    
    // Ensure models array exists and has defaults
    if (!merged.providerCredentials[provider].models || 
        merged.providerCredentials[provider].models.length === 0) {
      merged.providerCredentials[provider].models = [...DEFAULT_MODELS[provider]];
    }
  }
  
  return merged;
}

/**
 * Load BYOK state from localStorage
 * @returns {Object} The loaded state
 */
export function loadBYOKConfig() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    
    if (stored) {
      const parsedState = JSON.parse(stored);
      // Merge with defaults to ensure all fields exist
      state = mergeWithDefaults(parsedState);
    } else {
      // Initialize with defaults
      state = { ...DEFAULT_STATE };
    }
    
    stateLoaded = true;
    return { ...state };
  } catch (error) {
    console.error('Failed to load BYOK config:', error);
    state = { ...DEFAULT_STATE };
    stateLoaded = true;
    return { ...state };
  }
}

/**
 * Save BYOK state to localStorage
 * @param {Object} newState - The state to save (optional, uses cached state if not provided)
 */
export function saveBYOKConfig(newState = null) {
  try {
    if (newState) {
      state = { ...state, ...newState };
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save BYOK config:', error);
    throw new Error('Failed to save BYOK settings');
  }
}

/**
 * Ensure state is loaded
 */
function ensureLoaded() {
  if (!stateLoaded) {
    loadBYOKConfig();
  }
}

/**
 * Check if BYOK mode is enabled
 * @returns {boolean} True if BYOK is enabled
 */
export function getBYOKEnabled() {
  ensureLoaded();
  return state.byokEnabled === true;
}

/**
 * Enable or disable BYOK mode
 * @param {boolean} enabled - Whether BYOK should be enabled
 */
export function setBYOKEnabled(enabled) {
  ensureLoaded();
  state.byokEnabled = enabled;
  saveBYOKConfig();
}

/**
 * Get the currently active provider
 * @returns {string} Provider name ('openai' | 'anthropic' | 'openrouter' | 'huggingface' | 'ollama')
 */
export function getActiveProvider() {
  ensureLoaded();
  return state.activeProvider || 'openai';
}

/**
 * Set the active provider
 * @param {string} provider - Provider name ('openai' | 'anthropic' | 'openrouter' | 'huggingface' | 'ollama')
 */
export function setActiveProvider(provider) {
  ensureLoaded();
  const validProviders = ['openai', 'anthropic', 'openrouter', 'huggingface', 'ollama'];
  
  if (!validProviders.includes(provider)) {
    throw new Error(`Invalid provider: ${provider}. Must be one of: ${validProviders.join(', ')}`);
  }
  
  state.activeProvider = provider;
  
  // Also update active model to the provider's selected model
  const credentials = state.providerCredentials[provider];
  if (credentials?.selectedModel) {
    state.activeModel = credentials.selectedModel;
  }
  
  saveBYOKConfig();
}

/**
 * Get credentials for a specific provider
 * @param {string} provider - Provider name
 * @returns {Object|null} Provider credentials or null if not found
 */
export function getProviderCredentials(provider) {
  ensureLoaded();
  if (!state.providerCredentials || !state.providerCredentials[provider]) {
    return null;
  }
  return { ...state.providerCredentials[provider] };
}

/**
 * Update credentials for a specific provider
 * @param {string} provider - Provider name
 * @param {Object} credentials - Credentials to update (partial update supported)
 */
export function updateProviderCredentials(provider, credentials) {
  ensureLoaded();
  if (!state.providerCredentials) {
    state.providerCredentials = {};
  }
  
  if (!state.providerCredentials[provider]) {
    state.providerCredentials[provider] = {
      ...DEFAULT_STATE.providerCredentials[provider]
    };
  }
  
  state.providerCredentials[provider] = {
    ...state.providerCredentials[provider],
    ...credentials
  };
  
  saveBYOKConfig();
}

/**
 * Get the currently active model
 * @returns {string} Model identifier
 */
export function getActiveModel() {
  ensureLoaded();
  // Return cached active model or fall back to provider's selected model
  if (state.activeModel) {
    return state.activeModel;
  }
  
  const provider = getActiveProvider();
  const credentials = getProviderCredentials(provider);
  return credentials?.selectedModel || '';
}

/**
 * Set the active model
 * @param {string} model - Model identifier
 */
export function setActiveModel(model) {
  ensureLoaded();
  state.activeModel = model;
  
  // Also update the provider's selected model
  const provider = getActiveProvider();
  if (state.providerCredentials[provider]) {
    state.providerCredentials[provider].selectedModel = model;
  }
  
  saveBYOKConfig();
}

/**
 * Get credentials for the currently active provider
 * @returns {Object|null} Active provider credentials
 */
export function getActiveProviderCredentials() {
  ensureLoaded();
  return getProviderCredentials(state.activeProvider);
}

/**
 * Check if the active provider is properly configured (has required credentials)
 * @returns {boolean} True if provider is configured
 */
export function isActiveProviderConfigured() {
  ensureLoaded();
  const provider = getActiveProvider();
  const credentials = getProviderCredentials(provider);
  
  if (!credentials) return false;
  
  switch (provider) {
    case 'openai':
    case 'anthropic':
    case 'openrouter':
    case 'huggingface':
      return !!credentials.apiKey;
    case 'ollama':
      return !!credentials.endpoint;
    default:
      return false;
  }
}

/**
 * Check if BYOK mode is enabled and properly configured
 * @returns {boolean} True if using BYOK, false if using Page Notes service
 */
export function isBYOKMode() {
  return getBYOKEnabled() && isActiveProviderConfigured();
}

/**
 * Get the full current state (for debugging/export)
 * @returns {Object} Current state
 */
export function getBYOKConfig() {
  ensureLoaded();
  return { ...state };
}

/**
 * Reset BYOK state to defaults
 */
export function resetBYOKConfig() {
  state = { ...DEFAULT_STATE };
  saveBYOKConfig();
}

/**
 * Test connection to a provider (fetches models to verify credentials)
 * @param {string} provider - Provider name
 * @param {Object} credentials - Provider credentials
 * @returns {Promise<{success: boolean, message: string, models?: string[]}>}
 */
export async function testProviderConnection(provider, credentials) {
  try {
    let models = [];
    
    switch (provider) {
      case 'openai':
        models = await fetchOpenAIModels(credentials);
        break;
      case 'anthropic':
        // Anthropic doesn't have a public models endpoint, just return defaults
        models = [...DEFAULT_MODELS.anthropic];
        break;
      case 'openrouter':
        models = await fetchOpenRouterModels(credentials);
        break;
      case 'huggingface':
        // HuggingFace has many models, return curated defaults
        models = [...DEFAULT_MODELS.huggingface];
        break;
      case 'ollama':
        models = await fetchOllamaModels(credentials);
        break;
      default:
        throw new Error(`Unknown provider: ${provider}`);
    }
    
    return {
      success: true,
      message: `Connected successfully! Found ${models.length} models.`,
      models
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Connection failed'
    };
  }
}

/**
 * Fetch OpenAI models
 */
async function fetchOpenAIModels(credentials) {
  if (!credentials.apiKey) {
    throw new Error('OpenAI API key is required');
  }

  const response = await fetch('https://api.openai.com/v1/models', {
    headers: { 'Authorization': `Bearer ${credentials.apiKey}` }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`OpenAI API error (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  // Filter to chat models (gpt-*)
  return data.data
    .map(m => m.id)
    .filter(id => id.startsWith('gpt'))
    .sort();
}

/**
 * Fetch OpenRouter models
 */
async function fetchOpenRouterModels(credentials) {
  if (!credentials.apiKey) {
    throw new Error('OpenRouter API key is required');
  }

  const response = await fetch('https://openrouter.ai/api/v1/models', {
    headers: { 'Authorization': `Bearer ${credentials.apiKey}` }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`OpenRouter API error (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  return data.data.map(m => m.id).sort();
}

/**
 * Fetch Ollama models
 */
async function fetchOllamaModels(credentials) {
  const endpoint = (credentials.endpoint || 'http://localhost:11434').replace(/\/$/, '');
  
  const response = await fetch(`${endpoint}/api/tags`);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Ollama API error (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  return data.models.map(m => m.name);
}